<template>
    <Modal id="propoint-update-modal"
           title="PROPOINT_UPDATE.MODAL_TITLE"
           icon="fa-window">
        <div v-if="updateAvailable"
             class="alert alert-warning">
            <i class="fa-solid fa-circle-info"></i>
            {{ l(useUpdatePage ? 'PROPOINT_UPDATE.UPDATE_MESSAGE--UPDATE' : 'PROPOINT_UPDATE.UPDATE_MESSAGE--RELOAD') }}
        </div>
        <div v-else
             class="alert alert-success">
            <i class="fa-solid fa-check-circle"></i>
            {{ l('PROPOINT_UPDATE.UP_TO_DATE_MESSAGE') }}
        </div>

        <div class="comparison-cols">
            <div class="propoint-update-modal__version comparison-cols__col">
                <h4>{{ l('PROPOINT_UPDATE.VERSION_INSTALLED') }}</h4>

                <p>
                    <small>{{ l('PROPOINT_UPDATE.VERSION_COMMIT') }}</small>
                    {{ eposVersionCurrent.commit }}
                </p>

                <p>
                    <small>{{ l('PROPOINT_UPDATE.VERSION_BRANCH') }}</small>
                    {{ eposVersionCurrent.branch }}
                </p>

                <p>
                    <small>{{ l('PROPOINT_UPDATE.VERSION_BUILT_AT') }}</small>
                    {{ currentVersionBuiltAt }}
                </p>
            </div>

            <p>
                <i v-if="updateAvailable"
                   class="fa-solid fa-arrow-right"></i>
                <i v-else
                   class="fa-solid fa-equals"></i>
            </p>

            <div class="propoint-update-modal__version comparison-cols__col">
                <h4>{{ l('PROPOINT_UPDATE.VERSION_LATEST') }}</h4>

                <p>
                    <small>{{ l('PROPOINT_UPDATE.VERSION_COMMIT') }}</small>
                    {{ eposVersionLatest.commit }}
                </p>

                <p>
                    <small>{{ l('PROPOINT_UPDATE.VERSION_BRANCH') }}</small>
                    {{ eposVersionLatest.branch }}
                </p>

                <p>
                    <small>{{ l('PROPOINT_UPDATE.VERSION_BUILT_AT') }}</small>
                    {{ latestVersionBuiltAt }}
                </p>
            </div>
        </div>

        <template v-slot:footer>
            <div class="modl-footer">
                <SquareBtn size="lg"
                           class="cancel-btn"
                           @click="cancel">
                    {{ l('CANCEL') }}
                </SquareBtn>

                <SquareBtn size="lg"
                           @click="reset">
                    {{ l('PROPOINT_UPDATE.UPDATE_BTN--RESET') }}
                </SquareBtn>

                <SquareBtn size="lg"
                           :disabled="!updateAvailable"
                           color="success"
                           @click="update">
                    {{ l(useUpdatePage ? 'PROPOINT_UPDATE.UPDATE_BTN--UPDATE' : 'PROPOINT_UPDATE.UPDATE_BTN--RELOAD') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
import DateFormat from '@/lib/Dates/DateFormat';
import Modal from '@/vue/components/Modals/Modal';
import moment from 'moment';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { mapGetters } from 'vuex';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    name: 'ProPointUpdateModal',

    components: { Modal, SquareBtn },

    mixins: [
        ModalPropsMixin,
    ],

    computed: {
        ...mapGetters(['eposVersionCurrent', 'eposVersionLatest', 'updateAvailable']),

        /**
         * @return {string}
         */
        currentVersionBuiltAt() {
            return moment(this.eposVersionCurrent.builtAt).format(DateFormat.DATE_WITH_TIME_WITH_SECS);
        },

        /**
         * @return {string}
         */
        latestVersionBuiltAt() {
            return moment(this.eposVersionLatest.builtAt).format(DateFormat.DATE_WITH_TIME_WITH_SECS);
        },

        /**
         * Should the "update page" be used when the user clicks the Update Now button?
         * The update page is currently only designed to update the epos  app and not the other apps,
         * such as the kitchen.
         * If this is false then the Update Now button will be replaced with a simple Reload Now
         * button that will just reload the page.
         *
         * @returns {boolean}
         */
        useUpdatePage() {
            return container.appConfig.getCurrentScreen() === 'epos';
        },
    },

    methods: {
        /**
         * Trigger a ProPoint Reset
         *
         * @return {Promise<void>}
         */
        async reset() {
            const href = container.appConfig.getCurrentScreenUrl() + '/reset.html';
            window.location.href = href;
        },

        /**
         * An update is available.
         * If a service worker is installed and is available then redirect to the update page.
         * Otherwise, we fall back and just reload the page.
         *
         * This approach means it will be easy to integrate a device level setting at later time to determine whether or
         * not to use service workers.
         */
        async update() {
            if (!this.useUpdatePage) {
                // Just reload the page.
                window.location.reload();
                return;
            }

            let serviceWorkerInstalled = false;

            try {
                if ('serviceWorker' in navigator) {
                    const registrations = await navigator.serviceWorker.getRegistrations();
                    if (registrations && registrations.length > 0) {
                        serviceWorkerInstalled = true;
                    }
                }
            } catch (e) {
                // The service worker is *probably* not installed.
                // There may have been an exception thrown from the call to getRegistrations if the user deined
                // the permission.
            }

            if (serviceWorkerInstalled) {
                // There is a service worker installed. Send the user to the service worker update page.
                navigator.serviceWorker.ready.then((registration) => {
                    registration.update()
                        .then(() => {
                            const href = 'https://' + window.location.host + '/epos/service-worker-update.html';
                            console.log('Service worker: Update Now clicked. Redirecting to ' + href);
                            window.location.href = href;
                        });
                });
            } else {
                // Just reload the page.
                const href = container.appConfig.getCurrentScreenUrl() + '/update.html';
                window.location.href = href;
            }
        },
    },
};
</script>

<style lang="less">

@import (less, reference) "../../../less/variables.less";
@import (less, reference) "../../../less/mixins.less";

#propoint-update-modal {
    .propoint-update-modal__version {
        background: @light-grey;
        padding: @gutter-width;
        text-align: center;
        border-radius: @border-radius-base;

        h4 {
            margin: 0 0 @gutter-width;
            font-weight: bold;
        }

        p {
            margin: @gutter-width 0 0 0;
        }

        small {
            margin: 0 0 2px 0;
            display: block;
            font-weight: bold;
        }
    }
}
</style>
